import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { computeDelta, kpiRates, roundKpi } from './pickupTrackingLogic';
import type { DeltaKind, DeltaView } from './pickupTrackingLogic';
import type { TweenedKpi } from './useCountTween';
import type { TrackingKpiSnapshot } from './pickupTrackingTypes';
import styles from './PickupDriverTrackingPage.module.css';

type KpiKey = keyof TweenedKpi;

interface CardDef {
    key: KpiKey;
    dot: string;
    bad: boolean;
    deltaKind: DeltaKind;
    lowerIsBetter: boolean;
    /** 原型只有「已揽收」「失败」两张卡在数据变化时闪烁 */
    flashes: boolean;
}

const CARDS: CardDef[] = [
    { key: 'appointments', dot: '#22d3ee', bad: false, deltaKind: 'pct', lowerIsBetter: false, flashes: false },
    { key: 'pickedUp', dot: '#34d399', bad: false, deltaKind: 'pct', lowerIsBetter: false, flashes: true },
    { key: 'drivers', dot: '#22d3ee', bad: false, deltaKind: 'abs', lowerIsBetter: false, flashes: false },
    { key: 'failed', dot: '#f87171', bad: true, deltaKind: 'pct', lowerIsBetter: true, flashes: true },
];

const FLASH_MS = 1500;
const ZERO: TweenedKpi = { appointments: 0, pickedUp: 0, drivers: 0, failed: 0 };

interface TrackingKpiRowProps {
    /** 补间展示值；首个 tick 前为 null，按 0 展示 */
    shown: TweenedKpi | null;
    /** 当前 slot 目标值，用来判定「数据又涨了」触发闪烁、并与昨日对比 */
    current: TrackingKpiSnapshot | null;
    yesterday: TrackingKpiSnapshot | null;
    alertCount: number;
    alertOnly: boolean;
    onToggleAlert: () => void;
    /** 换枢纽 / 重新开屏时重置闪烁基线，避免把整页数据当成「新增」 */
    resetKey: string | null;
}

const deltaView = (card: CardDef, current: TrackingKpiSnapshot | null, yesterday: TrackingKpiSnapshot | null): DeltaView | null =>
    current && yesterday
        ? computeDelta(Number(current[card.key]) || 0, Number(yesterday[card.key]) || 0, card.deltaKind, card.lowerIsBetter)
        : null;

export const TrackingKpiRow: React.FC<TrackingKpiRowProps> = ({
    shown,
    current,
    yesterday,
    alertCount,
    alertOnly,
    onToggleAlert,
    resetKey,
}) => {
    const { t } = useTranslation();
    const [pulse, setPulse] = useState<Record<string, number | null>>({});
    const baseline = useRef<{ key: string | null; values: Partial<Record<KpiKey, number>> }>({
        key: null,
        values: {},
    });
    const timers = useRef<number[]>([]);

    useEffect(() => {
        if (!current) return;
        const base = baseline.current;
        if (base.key !== resetKey) {
            base.key = resetKey;
            base.values = {};
        }
        CARDS.forEach((c) => {
            const next = Number(current[c.key]) || 0;
            const prev = base.values[c.key];
            base.values[c.key] = next;
            // 首次拿到的值只做基线，不闪；只有变大才闪（成功/失败都是累计量，变小是数据修正，不庆祝）
            if (!c.flashes || prev === undefined || next <= prev) return;
            setPulse((p) => ({ ...p, [c.key]: next - prev }));
            timers.current.push(window.setTimeout(() => setPulse((p) => ({ ...p, [c.key]: null })), FLASH_MS));
        });
    }, [current, resetKey]);

    useEffect(
        () => () => {
            timers.current.forEach((id) => window.clearTimeout(id));
            timers.current = [];
        },
        [],
    );

    const values = roundKpi(shown ?? ZERO);
    const { okRate, failRate } = kpiRates(values);

    return (
        <div className={styles.kpirow}>
            {CARDS.map((c) => {
                const flashDelta = pulse[c.key] ?? null;
                const flashing = flashDelta !== null;
                const delta = deltaView(c, current, yesterday);
                const sub =
                    c.key === 'pickedUp'
                        ? t('pickup.driverTracking.kpi.subOk', { rate: okRate })
                        : c.key === 'failed'
                          ? t('pickup.driverTracking.kpi.subFail', { rate: failRate })
                          : '';
                return (
                    <div
                        key={c.key}
                        className={`${styles.panel} ${styles.kpi} ${c.bad ? styles.kpiBad : ''} ${flashing ? styles.kpiPulse : ''}`}
                    >
                        <div
                            className={styles.kpiGlow}
                            style={{ background: `linear-gradient(90deg,${c.dot},transparent 65%)` }}
                        />
                        <div className={styles.kpiSweep} />
                        <span className={`${styles.bump} ${flashing ? styles.bumpGo : ''}`}>
                            {flashing ? `+${Math.round(flashDelta ?? 0)}` : ''}
                        </span>
                        <div className={styles.kpiName}>
                            <span className={styles.kpiDot} style={{ background: c.dot }} />
                            <span className={styles.kpiNameText}>{t(`pickup.driverTracking.kpi.${c.key}`)}</span>
                            {c.key === 'drivers' ? (
                                <button
                                    type="button"
                                    className={`${styles.kchip} ${alertOnly ? styles.kchipOn : ''}`}
                                    title={t('pickup.driverTracking.kchipTip') || ''}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onToggleAlert();
                                    }}
                                >
                                    {t('pickup.driverTracking.kpi.subAlerts', { count: alertCount })}
                                </button>
                            ) : (
                                sub && <span className={styles.kpiSub}>{sub}</span>
                            )}
                        </div>
                        <div className={styles.kpiValRow}>
                            <b
                                className={`${styles.num} ${styles.kvB} ${c.bad ? styles.kvBad : ''} ${flashing ? styles.kvPulse : ''}`}
                            >
                                {values[c.key].toLocaleString()}
                            </b>
                            <em className={styles.kpiUnit}>{t(`pickup.driverTracking.kpi.unit.${c.key}`)}</em>
                            <span className={styles.kpiFoot}>
                                <span>{t('pickup.driverTracking.kpiFoot')}</span>
                                {delta ? (
                                    <span
                                        className={`${styles.kpiDelta} ${
                                            delta.tone === 'good' ? styles.up : delta.tone === 'bad' ? styles.down : styles.flat
                                        }`}
                                    >
                                        {`${delta.up ? '▲' : '▼'} ${delta.text}`}
                                    </span>
                                ) : (
                                    <span className={`${styles.kpiDelta} ${styles.flat}`}>-</span>
                                )}
                            </span>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};
